import { FeatureCard } from '@finansafe/design-system';
import {
  ArrowRight,
  BarChart3,
  Check,
  FileText,
  Lightbulb,
  PiggyBank,
  Target,
  TrendingUp,
  X,
} from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import PageHelmet from '../components/PageHelmet';
import { authService } from '../services/AuthService';

export default function HomePage() {
  useEffect(() => {
    // If user is authenticated, redirect to dashboard
    if (authService.isAuthenticated()) {
      window.location.href = '/dashboard';
    }
  }, []);

  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      featuresSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PageHelmet
        title="FinanSafe - Planejamento Financeiro Simplificado"
        description="Transforme suas finanças com planejamento inteligente. Crie planos de negócio, gerencie renda extra e tome decisões financeiras estratégicas."
        keywords={[
          'planejamento financeiro',
          'plano de negócio',
          'renda extra',
          'gestão financeira',
          'FinanSafe',
        ]}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Sua jornada financeira
                <span className="text-blue-600"> completa</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Descubra oportunidades de renda extra com IA, crie planos de
                negócio estruturados e gerencie suas finanças - tudo em uma
                única plataforma.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register" className="btn-primary text-center">
                  Começar Agora
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </Link>
                <button onClick={scrollToFeatures} className="btn-secondary text-center">
                  Ver Recursos
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>Gratuito para começar</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>Suporte em português</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-300">
                <div className="space-y-4">
                  <div className="h-4 bg-blue-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-32 bg-gradient-to-br from-blue-100 to-green-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-12 h-12 text-blue-600" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="h-16 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="h-16 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Target className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tudo que você precisa em um só lugar
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Três módulos integrados para transformar sua vida financeira
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={Lightbulb}
              title="Estratégias Inteligentes com IA"
              description="Nossa IA analisa seu perfil e sugere oportunidades personalizadas de renda extra ou estratégias de crescimento empresarial baseadas nas suas necessidades."
            />
            <FeatureCard
              icon={FileText}
              title="Crie seu Plano de Negócio"
              description="Wizard guiado que te ajuda a estruturar um plano de negócio completo, com projeções financeiras e análise de mercado."
            />
            <FeatureCard
              icon={PiggyBank}
              title="Gestão Financeira Empresarial"
              description="Dashboard completo para acompanhar receitas, despesas, metas e o crescimento dos seus novos negócios."
            />
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Por que escolher o FinanSafe?
            </h2>
            <p className="text-xl text-gray-600">
              Compare com outras soluções do mercado
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-sm border border-gray-200">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-6 font-semibold text-gray-900">
                    Recursos
                  </th>
                  <th className="text-center p-6 font-semibold text-blue-600">
                    FinanSafe
                  </th>
                  <th className="text-center p-6 font-semibold text-gray-600">
                    LivePlan
                  </th>
                  <th className="text-center p-6 font-semibold text-gray-600">
                    Nibo
                  </th>
                  <th className="text-center p-6 font-semibold text-gray-600">
                    Hotmart
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  [
                    'Descoberta de renda extra com IA',
                    true,
                    false,
                    false,
                    false,
                  ],
                  ['Plano de negócio guiado', true, true, false, false],
                  ['Gestão financeira integrada', true, false, true, false],
                  ['Suporte em português', true, false, true, true],
                  ['Preço acessível', true, false, true, true],
                  ['Tudo em uma plataforma', true, false, false, false],
                ].map(
                  ([feature, finansafe, liveplan, nibo, hotmart], index) => (
                    <tr
                      key={index}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="p-6 text-gray-900">{feature as string}</td>
                      <td className="p-6 text-center">
                        {finansafe ? (
                          <Check className="w-5 h-5 text-green-600 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-red-400 mx-auto" />
                        )}
                      </td>
                      <td className="p-6 text-center">
                        {liveplan ? (
                          <Check className="w-5 h-5 text-green-600 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-red-400 mx-auto" />
                        )}
                      </td>
                      <td className="p-6 text-center">
                        {nibo ? (
                          <Check className="w-5 h-5 text-green-600 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-red-400 mx-auto" />
                        )}
                      </td>
                      <td className="p-6 text-center">
                        {hotmart ? (
                          <Check className="w-5 h-5 text-green-600 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-red-400 mx-auto" />
                        )}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Pronto para transformar sua vida financeira?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Junte-se a milhares de brasileiros que já descobriram novas fontes
            de renda
          </p>
          <Link
            to="/register"
            className="inline-flex items-center bg-white text-blue-600 font-semibold py-4 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-lg"
          >
            Começar Gratuitamente
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img
                  src="/finansafe.png"
                  alt="FinanSafe Logo"
                  className="w-8 h-8 object-contain brightness-0 invert"
                />
                <span className="text-xl font-bold">FinanSafe</span>
              </div>
              <p className="text-gray-400">
                Sua jornada financeira completa em uma única plataforma.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Produto</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link
                    to="#features"
                    className="hover:text-white transition-colors"
                  >
                    Recursos
                  </Link>
                </li>
                <li>
                  <Link
                    to="/login"
                    className="hover:text-white transition-colors"
                  >
                    Entrar
                  </Link>
                </li>
                <li>
                  <Link
                    to="/register"
                    className="hover:text-white transition-colors"
                  >
                    Cadastrar
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Suporte</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link
                    to="/support"
                    className="hover:text-white transition-colors"
                  >
                    Central de Ajuda
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="hover:text-white transition-colors"
                  >
                    Contato
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacidade
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Termos de Uso
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 FinanSafe. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
